import { useEffect, useRef, useState } from "react";
import { FOAM_COLORS, SCULPT } from "@/config/designer";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { saveDesign } from "@/core/persistence/storage";
import { useDesignerStore } from "@/state/designerStore";
import {
  ExportIcon,
  HelpIcon,
  LockIcon,
  MenuIcon,
  NewIcon,
  RedoIcon,
  ResetIcon,
  SaveIcon,
  SoundOffIcon,
  SoundOnIcon,
  TrashIcon,
  UndoIcon,
} from "./Icons";
import { stopSculptSound } from "@/audio/carvingSound";
import { EthosLogo } from "./EthosLogo";

interface TopBarProps {
  engine: DesignerEngine;
  onExport: () => void;
  onNew: () => void;
}

export function TopBar({ engine, onExport, onNew }: TopBarProps): React.ReactElement {
  const canUndo = useDesignerStore((s) => s.canUndo);
  const canRedo = useDesignerStore((s) => s.canRedo);
  const exportStatus = useDesignerStore((s) => s.exportStatus);
  const setConfirmingReset = useDesignerStore((s) => s.setConfirmingReset);
  const setShowShortcuts = useDesignerStore((s) => s.setShowShortcuts);
  const soundEnabled = useDesignerStore((s) => s.soundEnabled);
  const toggleSound = useDesignerStore((s) => s.toggleSound);
  const baseLocked = useDesignerStore((s) => s.baseLocked);
  const toggleBaseLock = useDesignerStore((s) => s.toggleBaseLock);
  const baseThickness = useDesignerStore((s) => s.baseThickness);
  const setBaseThickness = useDesignerStore((s) => s.setBaseThickness);
  const foamColor = useDesignerStore((s) => s.foamColor);
  const setFoamColor = useDesignerStore((s) => s.setFoamColor);
  const notify = useDesignerStore((s) => s.notify);
  const [menuOpen, setMenuOpen] = useState(false);
  const [baseDepthOpen, setBaseDepthOpen] = useState(false);
  const [removingFloating, setRemovingFloating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const baseDepthRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen && !baseDepthOpen) return;
    const closeOutside = (event: PointerEvent): void => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
      if (!baseDepthRef.current?.contains(event.target as Node)) setBaseDepthOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setBaseDepthOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [baseDepthOpen, menuOpen]);

  const { width, depth, height } = engine.preset;
  const exporting = exportStatus.state === "working";

  return (
    <div className="hud__row">
      <div className="panel brand">
        <span className="brand__identity">
          <EthosLogo className="ethos-logo--toolbar" />
          <span className="brand__name">Hold Shaper V1.1</span>
        </span>
        <span className="brand__dims">
          {width} × {depth} × {height} mm · grid 50 mm
        </span>
      </div>

      <div className="panel toolbar">
        <div className="top-menu-wrap" ref={menuRef}>
          <button
            type="button"
            className="icon-btn icon-btn--menu"
            onClick={() => {
              setBaseDepthOpen(false);
              setMenuOpen((open) => !open);
            }}
            aria-label="Open model menu"
            aria-expanded={menuOpen}
            aria-haspopup="dialog"
          >
            <MenuIcon />
            <span>Menu</span>
          </button>

          {menuOpen ? (
            <div className="top-menu panel" role="dialog" aria-label="Model menu">
              <div className="top-menu__section">
                <span className="top-menu__heading">Design</span>
                <button
                  type="button"
                  className="top-menu__action"
                  onClick={() => {
                    setMenuOpen(false);
                    onNew();
                  }}
                >
                  <NewIcon size={17} />
                  New design
                </button>
                <button
                  type="button"
                  className="top-menu__action"
                  onClick={() => {
                    void saveDesign(engine.toSaved()).then((saved) => {
                      notify(
                        saved
                          ? "Design saved on this device."
                          : "This browser could not save the design.",
                        saved ? "info" : "warn",
                      );
                    });
                    setMenuOpen(false);
                  }}
                >
                  <SaveIcon size={17} />
                  Save design
                </button>
                <button
                  type="button"
                  className="top-menu__action"
                  onClick={() => {
                    if (engine.hasEdits) setConfirmingReset(true);
                    else engine.reset();
                    setMenuOpen(false);
                  }}
                >
                  <ResetIcon size={17} />
                  Reset block
                </button>
              </div>

              <div className="top-menu__section">
                <span className="top-menu__heading">Geometry</span>
                <button
                  type="button"
                  className="top-menu__action"
                  disabled={removingFloating}
                  onClick={() => {
                    setRemovingFloating(true);
                    window.setTimeout(() => {
                      try {
                        const result = engine.removeFloatingGeometry();
                        notify(
                          result.applied
                            ? `Removed ${result.removedComponents} floating ${result.removedComponents === 1 ? "piece" : "pieces"}.`
                            : (result.reason ?? "No floating geometry was found."),
                          result.applied ? "info" : "warn",
                        );
                      } finally {
                        setRemovingFloating(false);
                        setMenuOpen(false);
                      }
                    }, 30);
                  }}
                >
                  <TrashIcon size={17} />
                  {removingFloating ? "Checking…" : "Remove all floating geometry"}
                </button>
              </div>

              <div className="top-menu__section">
                <span className="top-menu__heading">Foam colour</span>
                <div className="colour-options" role="radiogroup" aria-label="Foam colour">
                  {FOAM_COLORS.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className="colour-option"
                      role="radio"
                      aria-checked={foamColor === option.id}
                      onClick={() => setFoamColor(option.id)}
                    >
                      <span className="colour-option__swatch" style={{ background: option.hex }} />
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="top-menu__section">
                <span className="top-menu__heading">App</span>
                <button
                  type="button"
                  className="top-menu__action"
                  onClick={() => {
                    setShowShortcuts(true);
                    setMenuOpen(false);
                  }}
                >
                  <HelpIcon size={17} />
                  Keyboard shortcuts
                </button>
                <button
                  type="button"
                  className="top-menu__action"
                  aria-pressed={soundEnabled}
                  onClick={() => {
                    if (soundEnabled) stopSculptSound();
                    toggleSound();
                    setMenuOpen(false);
                  }}
                >
                  {soundEnabled ? <SoundOnIcon size={17} /> : <SoundOffIcon size={17} />}
                  {soundEnabled ? "Sound on" : "Sound off"}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <span className="toolbar__divider" />

        <button
          type="button"
          className="icon-btn"
          onClick={() => engine.history.undo()}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          aria-label="Undo"
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={() => engine.history.redo()}
          disabled={!canRedo}
          title="Redo (Ctrl+Shift+Z)"
          aria-label="Redo"
        >
          <RedoIcon />
        </button>

        <div className="base-controls" ref={baseDepthRef}>
          <button
            type="button"
            className={`icon-btn icon-btn--lock${baseLocked ? " icon-btn--lock-active" : ""}`}
            onClick={() => {
              toggleBaseLock();
              notify(
                baseLocked
                  ? "Base unlocked. Side carving can now reach the floor."
                  : `Base locked. Sculpting will preserve at least ${baseThickness} mm of material.`,
              );
            }}
            title={baseLocked ? "Unlock base" : "Lock base"}
            aria-label={baseLocked ? "Unlock base" : "Lock base"}
            aria-pressed={baseLocked}
          >
            <LockIcon size={17} />
            <span>{baseLocked ? "Locked" : "Lock"}</span>
          </button>
          <button
            type="button"
            className="icon-btn icon-btn--base-depth"
            onClick={() => {
              setMenuOpen(false);
              setBaseDepthOpen((open) => !open);
            }}
            title="Set protected base depth"
            aria-label={`Set base depth, currently ${baseThickness} millimetres`}
            aria-expanded={baseDepthOpen}
            aria-haspopup="dialog"
          >
            {baseThickness} mm
          </button>
          {baseDepthOpen ? (
            <div className="base-depth-popover panel" role="dialog" aria-label="Base depth">
              <div className="control__head">
                <span className="top-menu__heading">Base depth</span>
                <span className="control__value">{baseThickness} mm</span>
              </div>
              <input
                type="range"
                min={SCULPT.minLockedBaseThickness}
                max={SCULPT.maxLockedBaseThickness}
                step={1}
                value={baseThickness}
                onChange={(event) => setBaseThickness(Number(event.target.value))}
                aria-label="Protected base depth in millimetres"
              />
              <span className="top-menu__note">Choose 1–10 mm, then use Lock to protect it.</span>
            </div>
          ) : null}
        </div>

        <span className="toolbar__divider" />

        <button
          type="button"
          className="icon-btn icon-btn--wide icon-btn--accent"
          onClick={onExport}
          disabled={exporting}
          title="Export STL (Ctrl+E)"
        >
          <ExportIcon />
          {exporting ? "Building…" : "Export STL"}
        </button>
      </div>
    </div>
  );
}
